import React from 'react'
import { useQuery } from '@apollo/client'
import { useParams, useHistory, Link } from 'react-router-dom'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  VpnKey as VpnKeyIcon,
} from '@material-ui/icons'
import dayjs from 'dayjs'
import { colors } from '../../../../Themes/Colors'
import { Cohort } from '../../../../Generated/graphql-types'
import GET_COHORT_BY_ID from '../../../../Apollo/Queries/GetCohortById'
import {
  PageContainer,
  HeaderSection,
  BackButton,
  TitleSection,
  Title,
  Subtitle,
  StatusChip,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  SectionTitle,
  InfoCard,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  ScrollablePanel,
  EmptyState,
  ActionBar,
  LoadingContainer,
  ErrorContainer,
} from './CohortDetail.styled'

interface RouteParams {
  id: string
}

const CohortDetail: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const cohortId = parseInt(id, 10)

  const { data, loading, error } = useQuery<{ cohort: Cohort }>(
    GET_COHORT_BY_ID,
    {
      variables: { id: cohortId },
      skip: !cohortId,
    }
  )

  const cohort = data?.cohort

  const handleBack = () => {
    history.push('/internal/self-serve/cohort-admin')
  }

  const navigateToFunders = () => {
    // Navigate to funder admin with cohort filter
    history.push(
      `/internal/self-serve/funder-admin?cohortId=${cohortId}&cohortName=${encodeURIComponent(
        cohort?.name || ''
      )}`
    )
  }

  const navigateToNonprofits = () => {
    // Navigate to nonprofit admin with cohort filter
    history.push(
      `/internal/self-serve/nonprofit-admin?cohortName=${encodeURIComponent(
        cohort?.name || ''
      )}`
    )
  }

  if (error) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Cohorts
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Error Loading Cohort
          </Typography>
          <Typography>{error.message}</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (loading) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Cohorts
        </BackButton>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (!cohort) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Cohorts
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Cohort Not Found
          </Typography>
          <Typography>Cohort with ID {cohortId} not found</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  const formatDate = (date: string | Date | null | undefined) =>
    date ? dayjs(date).format('MMM D, YYYY') : 'N/A'

  const formatDateTime = (date: string | Date | null | undefined) =>
    date ? dayjs(date).format('MMM D, YYYY h:mm A') : 'N/A'

  const isActive = () => {
    const now = dayjs()
    const start = cohort.startDate ? dayjs(cohort.startDate) : null
    const end = cohort.endDate ? dayjs(cohort.endDate) : null

    if (!start && !end) return true
    if (start && !end) return now.isAfter(start)
    if (!start && end) return now.isBefore(end)
    return start && end ? now.isAfter(start) && now.isBefore(end) : false
  }

  return (
    <PageContainer maxWidth='lg'>
      <HeaderSection>
        <BackButton
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant='text'
        >
          Back to Cohorts
        </BackButton>

        <TitleSection>
          <Box>
            <Box display='flex' alignItems='center'>
              <Title>{cohort.name || 'Unnamed Cohort'}</Title>
              <StatusChip
                label={isActive() ? 'Active' : 'Inactive'}
                status={isActive() ? 'active' : 'disabled'}
                size='small'
              />
            </Box>
            <Subtitle>Cohort ID: {cohort.id}</Subtitle>
          </Box>
        </TitleSection>
      </HeaderSection>

      {/* Statistics Cards */}
      <Grid container spacing={3} marginBottom={4}>
        <Grid item xs={12} sm={4}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <GroupIcon fontSize='large' />
              </StatIcon>
              <StatValue>{cohort.cohortOwnerships?.length || 0}</StatValue>
              <StatLabel>Funders</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <BusinessIcon fontSize='large' />
              </StatIcon>
              <StatValue>{cohort.cohortMemberships?.length || 0}</StatValue>
              <StatLabel>Nonprofits</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <VpnKeyIcon fontSize='large' />
              </StatIcon>
              <StatValue>{cohort.memberRoles?.length || 0}</StatValue>
              <StatLabel>Member Roles</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Basic Information */}
      <InfoCard>
        <CardContent>
          <SectionTitle>
            <InfoIcon />
            Basic Information
          </SectionTitle>
          <InfoGrid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Start Date</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDate(cohort.startDate)}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>End Date</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDate(cohort.endDate)}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Created</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDateTime(cohort.createdAt)}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDateTime(cohort.updatedAt)}
                </InfoValue>
              </InfoItem>
            </Grid>
            {cohort.memberRoles && cohort.memberRoles.length > 0 && (
              <Grid item xs={12}>
                <InfoItem>
                  <InfoLabel>Member Roles</InfoLabel>
                  <Box display='flex' flexWrap='wrap' gap={1} mt={1}>
                    {cohort.memberRoles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size='small'
                        icon={<VpnKeyIcon />}
                        color='primary'
                        variant='outlined'
                      />
                    ))}
                  </Box>
                </InfoItem>
              </Grid>
            )}
          </InfoGrid>
        </CardContent>
      </InfoCard>

      {/* Three column layout for related entities */}
      <Grid container spacing={3}>
        {/* Funders Panel */}
        <Grid item xs={12} md={6}>
          <SectionTitle>
            <GroupIcon />
            Associated Funders ({cohort.cohortOwnerships?.length || 0})
          </SectionTitle>
          <ScrollablePanel elevation={0}>
            {cohort.cohortOwnerships && cohort.cohortOwnerships.length > 0 ? (
              <List>
                {cohort.cohortOwnerships.filter(Boolean).map(
                  (ownership, index) =>
                    ownership && (
                      <ListItem
                        key={ownership.id || `ownership-${index}`}
                        divider
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: colors.resiliaBlue }}>
                            <GroupIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            ownership.funderOrg?.name ? (
                              <Link
                                to={`/internal/self-serve/funder-admin/${ownership.funderOrg.id}`}
                              >
                                {ownership.funderOrg.name}
                              </Link>
                            ) : (
                              'Unknown'
                            )
                          }
                          secondary={
                            <Box>
                              <Typography variant='caption' display='block'>
                                ID: {ownership.funderOrg?.id || 'N/A'}
                              </Typography>
                              {ownership.funderOrg?.disabled && (
                                <Chip
                                  label='Disabled'
                                  size='small'
                                  color='error'
                                  variant='outlined'
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    )
                )}
              </List>
            ) : (
              <EmptyState>
                <GroupIcon
                  style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}
                />
                <Typography variant='body2'>No funders associated</Typography>
              </EmptyState>
            )}
          </ScrollablePanel>
        </Grid>

        {/* Nonprofits Panel */}
        <Grid item xs={12} md={6}>
          <SectionTitle>
            <BusinessIcon />
            Associated Nonprofits ({cohort.cohortMemberships?.length || 0})
          </SectionTitle>
          <ScrollablePanel elevation={0}>
            {cohort.cohortMemberships && cohort.cohortMemberships.length > 0 ? (
              <List>
                {cohort.cohortMemberships.filter(Boolean).map(
                  (membership, index) =>
                    membership && (
                      <ListItem
                        key={membership.id || `membership-${index}`}
                        divider
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: colors.resiliaPurple }}>
                            <BusinessIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            membership.nonprofitOrg?.name ? (
                              <Link
                                to={`/internal/self-serve/nonprofit-admin/${membership.nonprofitOrg.id}`}
                              >
                                {membership.nonprofitOrg.name}
                              </Link>
                            ) : (
                              'Unknown'
                            )
                          }
                          secondary={
                            <Box>
                              <Typography variant='caption' display='block'>
                                ID: {membership.nonprofitOrg?.id || 'N/A'}
                              </Typography>
                              {(membership.startDate || membership.endDate) && (
                                <Typography variant='caption' display='block'>
                                  {membership.startDate &&
                                    `Start: ${formatDate(
                                      membership.startDate
                                    )}`}
                                  {membership.startDate &&
                                    membership.endDate &&
                                    ' | '}
                                  {membership.endDate &&
                                    `End: ${formatDate(membership.endDate)}`}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    )
                )}
              </List>
            ) : (
              <EmptyState>
                <BusinessIcon
                  style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}
                />
                <Typography variant='body2'>
                  No nonprofits associated
                </Typography>
              </EmptyState>
            )}
          </ScrollablePanel>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <ActionBar>
        <Button
          variant='outlined'
          color='primary'
          onClick={navigateToFunders}
          startIcon={<GroupIcon />}
          disabled={
            !cohort.cohortOwnerships || cohort.cohortOwnerships.length === 0
          }
        >
          View All Funders
        </Button>
        <Button
          variant='outlined'
          color='primary'
          onClick={navigateToNonprofits}
          startIcon={<BusinessIcon />}
          disabled={
            !cohort.cohortMemberships || cohort.cohortMemberships.length === 0
          }
        >
          View All Nonprofits
        </Button>
      </ActionBar>
    </PageContainer>
  )
}

export default CohortDetail
